// Iter 310 — stacked monthly commission by product type for one partner.
import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const COLORS = ["#059669", "#0ea5e9", "#f59e0b", "#8b5cf6", "#ec4899", "#14b8a6", "#94a3b8"];

export function ProductMixChart({ partnerKey, months = 12 }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setData(null); setErr("");
    api.get(`/commissions/partners/${encodeURIComponent(partnerKey)}/product-mix`, { params: { months } })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setErr("Product mix unavailable"));
    return () => { alive = false; };
  }, [partnerKey, months]);

  const products = data?.products || [];
  const hasData = !!data && data.series.some((s) => s.total > 0);

  return (
    <Card className="border-slate-200 shadow-sm p-3 mb-4" data-testid="partner-product-mix-card">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <div className="text-[10px] uppercase tracking-wider font-medium text-slate-500">
          Commission by product — last {months} months
        </div>
        {data && <div className="text-[10px] text-slate-400">{data.from} → {data.to}</div>}
      </div>
      <div className="w-full h-[240px]" data-testid="partner-product-mix-chart">
        {err ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-400">{err}</div>
        ) : !data ? (
          <div className="flex h-full items-center justify-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /></div>
        ) : !hasData ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-400">
            No line-level commission in this window — months imported from a Stax summary have no product split.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(p) => p.slice(2)} />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `$${v.toLocaleString()}`} width={70} />
              <Tooltip formatter={(v, name) => [money(v), name]}
                       labelFormatter={(p, payload) => `${p} · total ${money(payload?.[0]?.payload?.total)}`}
                       contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
              {products.map((p, i) => (
                <Bar key={p} dataKey={p} stackId="c" fill={p === "Other" ? COLORS[COLORS.length - 1] : COLORS[i % (COLORS.length - 1)]}
                     radius={i === products.length - 1 ? [3, 3, 0, 0] : 0} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}
